import {FormLabel} from '@mui/material';
import {
  UiWiseFormFieldOptionControl,
  UiWiseFormFieldType,
} from 'shared/constants/UiWiseConst';
import * as yup from 'yup';
import {isEmpty, notEmpty} from './CommonUtil';

export const getObjectValue = (obj, path) => {
  if (!obj || !path) return undefined;

  if (path.includes('.')) {
    return path.split('.').reduce((acc, key) => acc?.[key], obj);
  }
  return obj?.[path];
};

const getFieldValue = (field) => {
  const {value, datatype, fieldtype} = field;
  const isMulti = isMultiValueField(field);
  const isNumeric = ['number', 'float', 'percentage'].includes(datatype);

  // Not use isEmpty() because [],0, false are correct initial values for form.
  const isEmptyValue = value == null || value === '';

  if (isEmptyValue) {
    return isMulti ? [] : isNumeric ? 0 : '';
  }
  let result = [];
  if (Array.isArray(value)) {
    result = value;
  } else if (
    typeof value === 'string' &&
    fieldtype != UiWiseFormFieldType.TEXT &&
    value.includes(',')
  ) {
    result = value
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
  } else {
    result = [value];
  }

  if (isNumeric) {
    result = result.map((v) => {
      const p = parseFloat(v);
      return isNaN(p) ? 0 : p;
    });
  }

  if (isMulti) {
    return result;
  }
  return result[0];
};

export const getFormInitialValues = (fields, widgetData = {}) => {
  const values = {};

  fields.forEach((field) => {
    const dataValue = widgetData?.[field.name];
    let rawValue = notEmpty(dataValue) ? dataValue : field.value;

    values[field.name] = getFieldValue({
      ...field,
      value: rawValue,
    });
  });

  return values;
};

export const isMultiValueField = (field) => {
  const {optioncontrol, multivalue} = field;
  if (notEmpty(optioncontrol)) {
    switch (optioncontrol) {
      case UiWiseFormFieldOptionControl.RADIO:
        return false;
      default:
        return Boolean(multivalue);
    }
  }

  return field.fieldtype == 'imageUploader' ? multivalue : false;
};

export const getValidationRules = (fields) => {
  const schema = {};
  fields.forEach((f) => {
    const isSlider = UiWiseFormFieldOptionControl.SLIDER === f?.optioncontrol;
    const multivalued = isMultiValueField(f);

    switch (f.datatype) {
      case 'file':
        schema[f.name] = yup.mixed().label(f.label);
        if (f.required) schema[f.name] = schema[f.name].required();
        break;
      case 'number':
      case 'float':
      case 'percentage':
        if (multivalued) {
          schema[f.name] = yup
            .array()
            .label(f.label)
            .transform((v, originalValue) => {
              if (Array.isArray(v)) return v;
              if (originalValue === null || originalValue === undefined)
                return [];
              return [originalValue]; // fallback
            });

          if (f.required)
            if (isSlider) {
              schema[f.name] = schema[f.name].min(
                2,
                `${f.label} must have min and max values`,
              );
            } else {
              schema[f.name] = schema[f.name].min(
                1,
                `${f.label} is a required field`,
              );
            }
        } else {
          schema[f.name] = yup
            .number()
            .label(f.label)
            .transform((v) =>
              v === null || v === undefined || isNaN(v) ? 0 : v,
            );
          if (f.required) schema[f.name] = schema[f.name].required();
        }
        break;

      case 'datetype':
        schema[f.name] = yup
          .date()
          .nullable()
          .typeError(`${f.label} must be a valid date`)
          .label(f.label);
        if (f.required)
          schema[f.name] = schema[f.name].required(`${f.label} is required`);
        if (f.mindatetime)
          schema[f.name] = schema[f.name].min(
            new Date(f.mindatetime),
            `${f.label} must be after ${f.mindatetime}`,
          );
        if (f.maxdatetime)
          schema[f.name] = schema[f.name].max(
            new Date(f.maxdatetime),
            `${f.label} must be before ${f.maxdatetime}`,
          );
        break;

      default:
        if (multivalued) {
          schema[f.name] = yup.array().label(f.label);
          if (f.required)
            schema[f.name] = schema[f.name].min(1, `${f.label} is required`);
        } else {
          schema[f.name] = yup
            .string()
            .label(f.label)
            .transform((value, originalValue) => {
              if (typeof originalValue === 'object' && originalValue !== null) {
                return originalValue.value || originalValue.label || '';
              }
              return value;
            });

          if (f.required) {
            schema[f.name] = schema[f.name]
              .required(`${f.label} is required`)
              .test(
                'is-not-empty',
                `${f.label} cannot be only spaces`,
                (val) => val && val.toString().trim().length > 0,
              );
          }
        }
    }

    // Apply min/max for non-range fields
    if (!isSlider && f.datatype != 'file') {
      if (f.min > 0) schema[f.name] = schema[f.name].min(f.min);
      if (f.max > 0) schema[f.name] = schema[f.name].max(f.max);
    }

    // Regex / email
    if (f.match) schema[f.name] = schema[f.name].email();
    if (f.validationregex)
      schema[f.name] = schema[f.name].matches(
        new RegExp(f.validationregex),
        f.validationmessage,
      );
  });

  return yup.object().shape(schema);
};

export const evaluatePath = (expr, data) => {
  if (typeof expr !== 'string' || !data) return expr;

  // Matches ${propertyName}
  return expr.replace(/\${(.*?)}/g, (match, key) => {
    const value = key.split('.').reduce((obj, prop) => obj?.[prop], data);
    return value ?? match;
  });
};

export const getFieldLabel = (label, required) => {
  return label + (required ? ' * ' : ' ');
};

export const getFormFieldLabel = (props) => {
  return (
    props.label && (
      <FormLabel component='legend' labelid={props.labelid}>
        {getFieldLabel(props.label, props.required)}
      </FormLabel>
    )
  );
};

export const getOptFldValidInitValues = (value, optList, isMulti = false) => {
  // If the list is empty, return the value
  if (isEmpty(optList)) return value;

  const optionsList = optList?.map((opt) => opt.value);

  // MULTI SELECT
  if (isMulti) {
    let values = [];

    if (Array.isArray(value)) {
      values = value;
    } else if (typeof value === 'string' && value.length > 0) {
      values = value.split(',').map((v) => v.trim());
    }
    return values.filter((n) => optionsList.includes(n));
  }

  // SINGLE SELECT
  return optionsList.includes(value) ? value : '';
};

export const getSliderMarks = (min, max, step) => {
  if (min >= max || step <= 0) return false; // Do not show marks

  // Always: min + 3 middle labels + max
  const middleCount = 3;
  const range = max - min;
  const interval = range / (middleCount + 1);

  // Determine decimal precision (based on step or range)
  const precision =
    step && step < 1
      ? Math.max(0, step.toString().split('.')[1]?.length ?? 0)
      : Math.max(
          0,
          Math.max(
            min.toString().split('.')[1]?.length ?? 0,
            max.toString().split('.')[1]?.length ?? 0,
          ),
        );

  const round = (v) => Number(v.toFixed(precision));

  const result = [{value: min, label: round(min)}];

  for (let i = 1; i <= middleCount; i++) {
    const value = round(min + interval * i);
    result.push({value, label: value});
  }

  result.push({value: max, label: round(max)});

  return result;
};
